"use client";

import { Check, Clipboard } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { type MouseEvent, type ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type CopyButtonBaseProps = {
  text: string;
  variant?: "ghost" | "outline";
  className?: string;
  size?: React.ComponentProps<typeof Button>["size"];
};

export type CopyButtonProps = CopyButtonBaseProps &
  (
    | { presentation?: "icon"; children?: ReactNode }
    | { presentation: "inline"; children: ReactNode }
  );

export function CopyButton({
  text,
  variant = "ghost",
  className = "",
  children,
  size,
  presentation = "icon",
}: CopyButtonProps) {
  const resolvedSize = size ?? (children && presentation === "icon" ? "sm" : "icon");
  const [isCopied, setIsCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inlineClipboardEnterFromAbove, setInlineClipboardEnterFromAbove] = useState(false);

  const isInlineOverlayActive = isHovered || isFocused;
  const showInlineClipboardOverlay = !isCopied && isInlineOverlayActive;

  useEffect(() => {
    if (!inlineClipboardEnterFromAbove) return;
    const id = window.setTimeout(() => setInlineClipboardEnterFromAbove(false), 400);
    return () => window.clearTimeout(id);
  }, [inlineClipboardEnterFromAbove]);

  useEffect(() => {
    if (isHovered) {
      setIsFocused(false);
    }
  }, [isHovered]);

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setInlineClipboardEnterFromAbove(true);
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  if (presentation === "inline") {
    return (
      <motion.button
        aria-label="Copy to clipboard"
        className={cn(
          "relative inline-flex min-w-0 max-w-full cursor-pointer rounded-[inherit] border-0 bg-transparent p-0 text-left align-baseline font-inherit outline-none transition-[background-color,box-shadow]",
          "hover:bg-muted/25 focus-visible:bg-muted/25 focus-visible:ring-2 focus-visible:ring-ring/40",
          className,
        )}
        onBlur={() => setIsFocused(false)}
        onClick={handleCopy}
        onFocus={() => setIsFocused(true)}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        onPointerMove={() => setIsFocused(false)}
        type="button"
      >
        <span className="relative inline-block min-w-0 max-w-full">
          {children}
          <AnimatePresence mode="popLayout">
            {isCopied ? (
              <motion.span
                animate={{ opacity: 1, y: 0 }}
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 rounded-[inherit] bg-background/70 backdrop-blur-[1px]"
                exit={{ opacity: 0, y: 16 }}
                initial={{ opacity: 0, y: 16 }}
                key="inline-check"
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                <motion.span className="font-mono text-[11px] text-emerald-500 leading-snug">
                  Copied
                </motion.span>
                <Check className="size-3 shrink-0 text-emerald-500" strokeWidth={2} />
              </motion.span>
            ) : (
              <motion.span
                animate={{
                  opacity: showInlineClipboardOverlay ? 1 : 0,
                  y: showInlineClipboardOverlay ? 0 : 16,
                }}
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 rounded-[inherit] bg-background/70 backdrop-blur-[1px]"
                exit={{ opacity: 0, y: -16 }}
                initial={{
                  opacity: 0,
                  y: inlineClipboardEnterFromAbove ? -16 : 16,
                }}
                key="inline-clipboard-overlay"
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <motion.span className="font-mono text-[11px] leading-snug">Copy</motion.span>
                <Clipboard className="h-3 w-3 shrink-0" strokeWidth={2} />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </motion.button>
    );
  }

  return (
    <Button className={className} onClick={handleCopy} size={resolvedSize} variant={variant}>
      <AnimatePresence mode="popLayout">
        {isCopied ? (
          <motion.div
            animate={{ opacity: 1, y: [-16, 0] }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <Check className="h-3 w-3 shrink-0 text-emerald-500" strokeWidth={2} />
          </motion.div>
        ) : (
          <motion.div
            animate={{ opacity: 1, y: [16, 0] }}
            className="inline-flex items-center justify-center gap-1"
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {children ?? <Clipboard className="h-3 w-3" />}
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
}
